import { useEffect, useState } from 'react'
import { Checkbox, Form, Input } from 'antd'
import { CameraOutlined } from '@ant-design/icons'
import { Button, Drawer, toast } from '@crosscode/gravity-components'
import type { CashTerminal, CashTerminalForm, TerminalCameraBinding } from './types'
import { CashCameraPickerModal } from './CashCameraPickerModal'
import styles from '@/pages/CashPage.module.css'

interface Props {
  open: boolean
  storeId: number
  storeName?: string
  terminal: CashTerminal | null
  onClose: () => void
  onSubmit: (values: CashTerminalForm, terminalId?: string | number) => Promise<void>
}

export function CashFormDrawer({ open, storeId, storeName, terminal, onClose, onSubmit }: Props) {
  const [form] = Form.useForm<CashTerminalForm>()
  const editing = !!terminal
  const [pickerOpen, setPickerOpen] = useState(false)
  const [camera, setCamera] = useState<TerminalCameraBinding>({
    trassir_server_id: null,
    trassir_channel_guid: null,
    trassir_channel_name: null,
  })

  useEffect(() => {
    if (!open) return
    if (terminal) {
      const nextCamera: TerminalCameraBinding = {
        trassir_server_id: terminal.trassir_server_id ?? null,
        trassir_channel_guid: terminal.trassir_channel_guid ?? null,
        trassir_channel_name: terminal.trassir_channel_name ?? null,
      }
      setCamera(nextCamera)
      form.setFieldsValue({
        object_id: storeId,
        num: String(terminal.num ?? ''),
        ip: terminal.ip || '',
        kso: Boolean(terminal.type?.includes('УКМ-5')),
        comment: terminal.comment || '',
        ...nextCamera,
      })
    } else {
      const empty: TerminalCameraBinding = {
        trassir_server_id: null,
        trassir_channel_guid: null,
        trassir_channel_name: null,
      }
      setCamera(empty)
      form.setFieldsValue({
        object_id: storeId,
        num: '',
        ip: '',
        kso: false,
        comment: '',
        ...empty,
      })
    }
  }, [open, terminal, storeId, form])

  const handleFinish = async (values: CashTerminalForm) => {
    try {
      await onSubmit(
        {
          ...values,
          object_id: storeId,
          num: String(values.num).trim(),
          ip: String(values.ip).trim(),
          comment: values.comment?.trim() || '',
          trassir_server_id: camera.trassir_server_id,
          trassir_channel_guid: camera.trassir_channel_guid,
          trassir_channel_name: camera.trassir_channel_name,
        },
        terminal?.id,
      )
      toast.success(editing ? 'Касса обновлена' : 'Касса добавлена')
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка сохранения')
    }
  }

  const cameraLabel = camera.trassir_channel_name || camera.trassir_channel_guid

  return (
    <>
      <Drawer
        title={editing ? `Редактирование · ${terminal?.num ?? terminal?.id}` : 'Новая касса'}
        open={open}
        onClose={onClose}
        size={480}
      >
        {storeName ? <p className={styles.formStoreHint}>Магазин: {storeName}</p> : null}
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item name="object_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="num"
            label="Номер кассы"
            rules={[{ required: true, message: 'Укажите номер' }]}
          >
            <Input placeholder="1" />
          </Form.Item>
          <Form.Item
            name="ip"
            label="IP адрес"
            rules={[
              { required: true, message: 'Укажите IP' },
              {
                pattern:
                  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                message: 'Некорректный IP',
              },
            ]}
          >
            <Input placeholder="192.168.1.10" />
          </Form.Item>
          <Form.Item name="kso" valuePropName="checked">
            <Checkbox>Касса самообслуживания (УКМ‑5)</Checkbox>
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={2} placeholder="Необязательно" />
          </Form.Item>

          <div className={styles.cameraField}>
            <div className={styles.cameraFieldHead}>
              <span className={styles.cameraFieldLabel}>Камера TRASSIR</span>
              <Button size="small" icon={<CameraOutlined />} onClick={() => setPickerOpen(true)}>
                {cameraLabel ? 'Изменить' : 'Выбрать…'}
              </Button>
            </div>
            <p className={styles.cameraFieldValue}>
              {cameraLabel ? cameraLabel : 'Не выбрана'}
            </p>
          </div>

          <div className={styles.drawerActions}>
            <Button type="primary" htmlType="submit">
              {editing ? 'Сохранить' : 'Добавить'}
            </Button>
            <Button onClick={onClose}>Отмена</Button>
          </div>
        </Form>
      </Drawer>

      <CashCameraPickerModal
        open={pickerOpen}
        storeId={storeId}
        terminalLabel={form.getFieldValue('num') || terminal?.num}
        initial={camera}
        onClose={() => setPickerOpen(false)}
        onSave={(binding) => {
          setCamera(binding)
          form.setFieldsValue(binding)
        }}
      />
    </>
  )
}
